<div class="appointments-container">
  <!-- Loading state -->
  <div class="loading-state" *ngIf="loading">
    <div class="loading-message">
      <mat-spinner [diameter]="40" color="primary"></mat-spinner>
      <h3>{{loadingStatus}}</h3>
    </div>
  </div>

  <!-- Content when loaded -->
  <div class="appointments-content" *ngIf="!loading">
    <!-- Header with action button -->
    <div class="appointments-header">
      <h1 class="page-title">My Appointments</h1>
      <button mat-raised-button color="primary" class="new-appointment-button" (click)="bookAppointment()">
        <mat-icon>add</mat-icon>
        Schedule New Appointment
      </button>
    </div>

    <!-- Main appointment sections -->
    <div class="appointments-sections">
      <!-- Upcoming appointments section -->
      <section class="appointment-section upcoming-section" [class.empty-section]="upcomingAppointments.length === 0">
        <div class="section-header">
          <h2>
            <mat-icon class="section-icon">event_available</mat-icon>
            Upcoming Appointments
          </h2>
          <span class="appointment-count" *ngIf="upcomingAppointments.length > 0">
            {{upcomingAppointments.length}} appointment<span *ngIf="upcomingAppointments.length !== 1">s</span>
          </span>
        </div>

        <!-- No upcoming appointments message -->
        <div class="empty-message" *ngIf="upcomingAppointments.length === 0">
          <mat-icon>event_busy</mat-icon>
          <p>You don't have any upcoming appointments scheduled.</p>
          <button mat-stroked-button color="primary" (click)="bookAppointment()">
            Schedule an Appointment
          </button>
        </div>

        <!-- Upcoming appointments list -->
        <div class="appointments-list" *ngIf="upcomingAppointments.length > 0">
          <div class="appointment-card" *ngFor="let appointment of upcomingAppointments">
            <!-- Card header with date and time -->
            <div class="card-header" [ngClass]="getStatusColor(appointment.appointmentStatus, appointment)">
              <div class="appointment-date">
                <div class="date-month">{{formatMonth(appointment.appointmentDateTime)}}</div>
                <div class="date-day">{{formatDay(appointment.appointmentDateTime)}}</div>
              </div>
              
              <div class="appointment-time">
                <div class="time-display">{{formatTime(appointment.appointmentDateTime)}}</div>
                <div class="duration-display">{{appointment.lengthInMinutes}} minutes</div>
              </div>
            </div>

            <!-- Card body with appointment details -->
            <div class="card-body">
              <div class="appointment-type">
                <mat-icon>medical_services</mat-icon>
                <span>{{appointment.appointmentType || 'General Appointment'}}</span>
              </div>

              <div class="appointment-status" [ngClass]="getStatusColor(appointment.appointmentStatus, appointment)">
                <mat-icon>{{getStatusIcon(appointment.appointmentStatus, appointment)}}</mat-icon>
                <span>{{getStatusDisplay(appointment.appointmentStatus, appointment)}}</span>
              </div>

              <div class="appointment-info">
                <span class="info-label">Insurance:</span>
                <span class="info-value">{{appointment.insuranceStatus || 'Not specified'}}</span>
              </div>
            </div>

            <!-- Card actions -->
            <div class="card-actions">
              <button mat-stroked-button class="confirm-button"
                      (click)="confirmAppointment(appointment.appointmentNumber.toString())"
                      *ngIf="appointment.appointmentStatus === 'Scheduled' && appointment.confirmed !== 'Confirmed'">
                <mat-icon>check_circle</mat-icon>
                Confirm
              </button>

              <button mat-stroked-button class="reschedule-button"
                      (click)="rescheduleAppointment(appointment.appointmentNumber.toString())">
                <mat-icon>schedule</mat-icon>
                Reschedule
              </button>

              <button mat-stroked-button class="cancel-button"
                      (click)="cancelAppointment(appointment.appointmentNumber.toString())"
>
                <mat-icon>cancel</mat-icon>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- Planned appointments section (if any) -->
      <section class="appointment-section planned-section" *ngIf="plannedAppointments.length > 0">
        <div class="section-header">
          <h2>
            <mat-icon class="section-icon">calendar_today</mat-icon>
            Planned Appointments
          </h2>
          <span class="appointment-count">
            {{plannedAppointments.length}} appointment<span *ngIf="plannedAppointments.length !== 1">s</span>
          </span>
        </div>

        <!-- Planned appointments list -->
        <div class="appointments-list">
          <div class="appointment-card planned-card" *ngFor="let appointment of plannedAppointments">
            <!-- Card header -->
            <div class="card-header" [ngClass]="getStatusColor(appointment.appointmentStatus, appointment)">
              <div class="appointment-date">
                <mat-icon>event_note</mat-icon>
              </div>
              
              <div class="appointment-time">
                <div class="time-display">Needs Scheduling</div>
                <div class="duration-display">{{appointment.lengthInMinutes}} minutes</div>
              </div>
            </div>

            <!-- Card body -->
            <div class="card-body">
              <div class="appointment-type">
                <mat-icon>medical_services</mat-icon>
                <span>{{appointment.appointmentType || 'General Appointment'}}</span>
              </div>

              <div class="appointment-status" [ngClass]="getStatusColor(appointment.appointmentStatus, appointment)">
                <mat-icon>{{getStatusIcon(appointment.appointmentStatus, appointment)}}</mat-icon>
                <span>{{getStatusDisplay(appointment.appointmentStatus, appointment)}}</span>
              </div>

              <div class="appointment-info">
                <span class="info-label">Insurance:</span>
                <span class="info-value">{{appointment.insuranceStatus || 'Not specified'}}</span>
              </div>
            </div>

            <!-- Card actions -->
            <div class="card-actions">
              <button mat-stroked-button class="schedule-button"
                      (click)="reschedulePlannedAppointment(appointment.appointmentNumber.toString())">
                <mat-icon>event_available</mat-icon>
                Schedule Now
              </button>

              <button mat-stroked-button class="cancel-button"
                      (click)="cancelAppointment(appointment.appointmentNumber.toString())"
>
                <mat-icon>cancel</mat-icon>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- Past appointments section -->
      <section class="appointment-section past-section" [class.empty-section]="pastAppointments.length === 0">
        <div class="section-header">
          <h2>
            <mat-icon class="section-icon">history</mat-icon>
            Past Appointments
          </h2>
          <span class="appointment-count" *ngIf="pastAppointments.length > 0">
            {{pastAppointments.length}} appointment<span *ngIf="pastAppointments.length !== 1">s</span>
          </span>
        </div>

        <!-- No past appointments message -->
        <div class="empty-message" *ngIf="pastAppointments.length === 0">
          <mat-icon>history</mat-icon>
          <p>You don't have any past appointment records.</p>
        </div>

        <!-- Past appointments list -->
        <div class="appointments-list" *ngIf="pastAppointments.length > 0">
          <div class="appointment-card past-card" *ngFor="let appointment of pastAppointments">
            <!-- Card header -->
            <div class="card-header" [ngClass]="getStatusColor(appointment.appointmentStatus, appointment)">
              <div class="appointment-date">
                <div class="date-month">{{formatMonth(appointment.appointmentDateTime)}}</div>
                <div class="date-day">{{formatDay(appointment.appointmentDateTime)}}</div>
              </div>
              
              <div class="appointment-time">
                <div class="time-display">{{formatTime(appointment.appointmentDateTime)}}</div>
                <div class="duration-display">{{appointment.lengthInMinutes}} minutes</div>
              </div>
            </div>

            <!-- Card body -->
            <div class="card-body">
              <div class="appointment-type">
                <mat-icon>medical_services</mat-icon>
                <span>{{appointment.appointmentType || 'General Appointment'}}</span>
              </div>

              <div class="appointment-status" [ngClass]="getStatusColor(appointment.appointmentStatus, appointment)">
                <mat-icon>{{getStatusIcon(appointment.appointmentStatus, appointment)}}</mat-icon>
                <span>{{getStatusDisplay(appointment.appointmentStatus, appointment)}}</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</div>